<template>
  <div class="py-5">
    <h1 class="display-4">Corporate Action Calculator</h1>

    <!-- Script Search Section -->
    <div class="mt-4">
      <div class="search-wrapper position-relative mb-4">
        <label class="form-label fw-bold">Select Stock</label>
        <div class="position-relative">
          <input
            type="text"
            class="form-control"
            placeholder="Type to search stocks..."
            v-model="searchTerm"
            :disabled="!!selectedScript"
          />
          <div
            class="dropdown-menu w-100"
            :class="{'show': searchResults.length > 0 && !selectedScript}"
            style="max-height: 300px; overflow-y: auto;"
          >
            <button
              v-for="script in searchResults"
              :key="script._id"
              class="dropdown-item"
              type="button"
              @click="selectScript(script)"
            >
              <strong>{{ script.code }}</strong> - {{ script.name }}
            </button>
          </div>
        </div>
        <div v-if="selectedScript" class="alert alert-success mt-2 d-flex justify-content-between align-items-center">
          <span><strong>Selected:</strong> {{ selectedScript.code }} - {{ selectedScript.name }}</span>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="clearSelection">
            <i class="fa-solid fa-xmark"></i> Clear
          </button>
        </div>
      </div>

      <!-- Form Section -->
      <form @submit.prevent="handleSubmit" class="mt-4">
        <div class="row mb-3">
          <div class="col-md-6">
            <label for="action-type" class="form-label fw-bold">Action Type</label>
            <select
              id="action-type"
              class="form-select"
              v-model="actionType"
              required
            >
              <option value="Split">Split</option>
              <option value="Bonus">Bonus</option>
            </select>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="value1" class="form-label fw-bold">{{ field1Label }}</label>
            <input
              id="value1"
              type="number"
              class="form-control"
              v-model.number="value1"
              :placeholder="field1Label"
              step="0.01"
              min="0.01"
              required
            />
          </div>
          <div class="col-md-6">
            <label for="value2" class="form-label fw-bold">{{ field2Label }}</label>
            <input
              id="value2"
              type="number"
              class="form-control"
              v-model.number="value2"
              :placeholder="field2Label"
              step="0.01"
              min="0.01"
              required
            />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="action-date" class="form-label fw-bold">Corporate Action Date</label>
            <input
              id="action-date"
              type="date"
              class="form-control"
              v-model="selectedDate"
              required
            />
          </div>
        </div>

        <!-- Ratio Display -->
        <div v-if="calculatedRatio" class="mb-4 p-3 bg-light border rounded">
          <div class="row">
            <div class="col-md-6">
              <h6 class="mb-1">Ratio</h6>
              <p class="fs-4 mb-0 text-primary fw-bold">{{ calculatedRatio }}</p>
            </div>
            <div class="col-md-6">
              <h6 class="mb-1">Value</h6>
              <p class="fs-4 mb-0 text-primary fw-bold">{{ calculatedRatio }}</p>
            </div>
          </div>
        </div>

        <!-- Calculate Button -->
        <button
          type="submit"
          class="btn btn-primary btn-lg"
          :disabled="!canSubmit || loading"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <span v-if="loading">Calculating EMAs...</span>
          <span v-else>Calculate EMAs</span>
        </button>
        <button
          type="button"
          class="btn btn-secondary btn-lg ms-3"
          @click="resetForm"
        >
          Reset
        </button>
      </form>

      <!-- Results Section -->
      <div v-if="apiResponse" class="mt-5">
        <div class="alert alert-warning border-warning">
          <i class="fa-solid fa-triangle-exclamation me-2"></i>
          <strong>Warning:</strong> Updating the historical data is irreversible. Please ensure the calculations are correct before applying the changes.
        </div>

        <h3 class="mb-4">Results for {{ apiResponse.stock.name }} ({{ apiResponse.stock.code }})</h3>

        <div class="alert alert-info">
          <p class="mb-1"><strong>Corporate Action Date:</strong> {{ formatDate(apiResponse.dateRange.corporateActionDate) }}</p>
          <p class="mb-1"><strong>Records Exported:</strong> {{ apiResponse.dateRange.recordsExported }}</p>
          <p class="mb-0"><strong>Date Range:</strong> {{ formatDate(apiResponse.dateRange.earliest) }} to {{ formatDate(apiResponse.dateRange.latest) }}</p>
        </div>

        <!-- Existing Corporate Actions Table -->
        <div v-if="corporateActions.length > 0" class="mt-4">
          <h4>Existing Corporate Actions</h4>
          <div class="table-responsive">
            <table class="table table-hover table-bordered">
              <thead class="table-light">
                <tr>
                  <th class="text-center">#</th>
                  <th class="text-center">Stock</th>
                  <th class="text-center">Action Date</th>
                  <th class="text-center">Type of Action</th>
                  <th class="text-center">Ratio</th>
                  <th class="text-center">Created At</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(action, index) in corporateActions" :key="action._id">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td class="text-center">{{ action.script }}</td>
                  <td class="text-center">{{ formatDate(action.actionDate) }}</td>
                  <td class="text-center text-capitalize">{{ action.typeOfAction }}</td>
                  <td class="text-end">{{ action.ratio }}</td>
                  <td class="text-center">{{ formatDate(action.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="alert alert-secondary mt-4">
          <p class="mb-0">No existing corporate actions found for this stock.</p>
        </div>

        <!-- Latest Daily EMAs Table -->
        <h4 class="mt-4">Latest Daily EMAs</h4>
        <div class="table-responsive">
          <table class="table table-hover table-bordered">
            <thead class="table-light">
              <tr>
                <th class="text-center">Day 4 EMA</th>
                <th class="text-center">Day 13 EMA</th>
                <th class="text-center">Day 40 EMA</th>
                <th class="text-center">Day 80 EMA</th>
                <th class="text-center">Day 200 EMA</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.daily.Day4EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.daily.Day13EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.daily.Day40EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.daily.Day80EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.daily.Day200EMA) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Latest Weekly EMAs Table -->
        <h4 class="mt-4">Latest Weekly EMAs</h4>
        <div class="table-responsive">
          <table class="table table-hover table-bordered">
            <thead class="table-light">
              <tr>
                <th class="text-center">Week 4 EMA</th>
                <th class="text-center">Week 13 EMA</th>
                <th class="text-center">Week 40 EMA</th>
                <th class="text-center">Week 80 EMA</th>
                <th class="text-center">Week 200 EMA</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.weekly.Week4EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.weekly.Week13EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.weekly.Week40EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.weekly.Week80EMA) }}</td>
                <td class="text-end">{{ roundOff(apiResponse.latestEMAs.weekly.Week200EMA) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Update Button -->
        <div class="mt-4">
          <button
            type="button"
            class="btn btn-danger btn-lg"
            @click="applyEMAs"
            :disabled="applyLoading"
          >
            <span v-if="applyLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span v-if="applyLoading">Applying Changes...</span>
            <span v-else>Update Historical Data</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import { debounce } from 'vue-debounce';
import moment from "moment-timezone";

export default {
  name: "CorporateActionView",
  data: () => ({
    // Search
    searchTerm: '',
    searchResults: [],
    selectedScript: null,
    searchLoading: false,
    debouncedSearch: null,

    // Form
    actionType: 'Split',
    value1: '',
    value2: '',
    selectedDate: '',

    // API
    loading: false,
    applyLoading: false,
    apiResponse: null,
    corporateActions: []
  }),
  computed: {
    field1Label() {
      return this.actionType === 'Split' ? 'Old Face Value' : 'Existing Shares';
    },
    field2Label() {
      return this.actionType === 'Split' ? 'New Face Value' : 'Shares';
    },
    calculatedRatio() {
      if (!this.value1 || !this.value2) return null;
      const v1 = parseFloat(this.value1);
      const v2 = parseFloat(this.value2);

      if (isNaN(v1) || isNaN(v2) || v1 <= 0 || v2 <= 0) return null;

      if (this.actionType === 'Split') {
        // Split: ratio = New Face Value / Old Face Value
        return (v2 / v1).toFixed(4);
      } else {
        // Bonus: ratio = Existing Shares / (Existing Shares + Bonus Shares)
        return (v1 / (v1 + v2)).toFixed(4);
      }
    },
    canSubmit() {
      return this.selectedScript && this.value1 && this.value2 && this.selectedDate && this.calculatedRatio;
    }
  },
  watch: {
    searchTerm() {
      if (this.debouncedSearch) {
        this.debouncedSearch();
      }
    },
    actionType() {
      // Clear values when switching action type
      this.value1 = '';
      this.value2 = '';
    }
  },
  methods: {
    async searchScripts() {
      if (!this.searchTerm || this.searchTerm.length < 2) {
        this.searchResults = [];
        return;
      }

      try {
        this.searchLoading = true;
        const res = await axios.get(`/api/scripts/lookup?offset=0&limit=10&term=${encodeURIComponent(this.searchTerm)}`);
        this.searchResults = res.data.paginatedResult.data;
      } catch (error) {
        console.error('Search error:', error);
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
      }
    },
    selectScript(script) {
      this.selectedScript = script;
      this.searchResults = [];
      this.searchTerm = '';
    },
    clearSelection() {
      this.selectedScript = null;
      this.searchTerm = '';
    },
    resetForm() {
      this.selectedScript = null;
      this.searchTerm = '';
      this.searchResults = [];
      this.actionType = 'Split';
      this.value1 = '';
      this.value2 = '';
      this.selectedDate = '';
      this.apiResponse = null;
      this.corporateActions = [];
    },
    validateForm() {
      if (!this.selectedScript) {
        alert('Please select a script');
        return false;
      }
      if (!this.selectedDate) {
        alert('Please select a date');
        return false;
      }
      if (!this.value1 || parseFloat(this.value1) <= 0) {
        alert(`Please enter a valid ${this.field1Label}`);
        return false;
      }
      if (!this.value2 || parseFloat(this.value2) <= 0) {
        alert(`Please enter a valid ${this.field2Label}`);
        return false;
      }
      return true;
    },
    async handleSubmit() {
      if (!this.validateForm()) return;

      try {
        this.loading = true;

        // First, fetch existing corporate actions
        const corporateActionsResponse = await axios.get(`/api/task/corporate-actions/${this.selectedScript._id}`);
        this.corporateActions = corporateActionsResponse.data.corporateActions || [];

        // Then calculate EMAs with split
        const payload = {
          stockName: this.selectedScript.code,
          date: this.selectedDate,
          splitRatio: parseFloat(this.calculatedRatio)
        };

        const response = await axios.post('/api/task/calculate-emas-with-split', payload);
        this.apiResponse = response.data;

      } catch (error) {
        alert(error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
      }
    },
    async applyEMAs() {
      const confirmed = confirm('Are you sure you want to update the historical data? This action is irreversible!');
      if (!confirmed) return;

      try {
        this.applyLoading = true;

        const payload = {
          stockName: this.selectedScript.code,
          date: this.selectedDate,
          splitRatio: parseFloat(this.calculatedRatio),
          typeOfAction: this.actionType
        };

        await axios.post('/api/task/apply-emas-with-split', payload);

        alert('Historical data updated successfully!');

        // Optionally reset the form after successful update
        this.resetForm();

      } catch (error) {
        alert(error.response?.data?.message || error.message);
      } finally {
        this.applyLoading = false;
      }
    },
    roundOff(number) {
      return parseFloat(number).toFixed(2);
    },
    formatDate(timestamp) {
      return moment.utc(timestamp).tz("Asia/Kolkata").format('DD-MMM-YYYY');
    }
  },
  mounted() {
    this.debouncedSearch = debounce(() => {
      this.searchScripts();
    }, '500ms');
  }
}
</script>

<style scoped>
.search-wrapper .dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  width: 100%;
}

.dropdown-item:hover {
  cursor: pointer;
}

.table-responsive {
  overflow-x: auto;
}

td, th {
  vertical-align: middle;
}
</style>
